// Normalizes attendance clock-in/out photo URLs before they are used as an
// <img src>. The API stores whatever string the upload service returns, and
// mobile-app uploads have shown a few defects that break verbatim rendering:
//   - a bare S3 key with no host ("dev/fileIn-...jpeg") -> resolves against the
//     CRM origin instead of S3
//   - http:// on an https:// page -> blocked as mixed content
//   - protocol-relative ("//bucket.s3...") values
//   - stray surrounding whitespace or quotes
//   - inconsistent percent-encoding of the colons in the timestamped filename
//
// NOTE: encoding is normalized only for consistency. S3 percent-decodes the
// request path, so "15%3A01", "15:01" and the mixed form all resolve to the
// same object; re-encoding does NOT fix an object that is missing or not
// publicly readable (those return AccessDenied regardless and must be fixed in
// the upload service / bucket policy).

// Base used to resolve bare keys. Override per-environment if the bucket moves.
const S3_BASE_URL = (
	process.env.REACT_APP_S3_BASE_URL ||
	'https://bestnationalcarpots.s3.us-west-1.amazonaws.com'
).replace(/\/+$/, '');

export function normalizePhotoUrl(value) {
	if (typeof value !== 'string') {
		return '';
	}

	// Trim whitespace and any surrounding quotes the API may have stored.
	let raw = value.trim().replace(/^["']+|["']+$/g, '');
	if (!raw) {
		return '';
	}

	// Protocol-relative -> https.
	if (raw.startsWith('//')) {
		raw = `https:${raw}`;
	}

	// Bare S3 key (no scheme) -> prepend the bucket base.
	if (!/^https?:\/\//i.test(raw)) {
		raw = `${S3_BASE_URL}/${raw.replace(/^\/+/, '')}`;
	}

	let url;
	try {
		url = new URL(raw);
	} catch {
		// Not parseable as a URL; return the cleaned string and let the browser
		// (or the onError fallback) deal with it.
		return raw;
	}

	// Force https to avoid mixed-content blocking on the https CRM.
	if (url.protocol === 'http:') {
		url.protocol = 'https:';
	}

	// Re-encode each path segment consistently (decode-then-encode), so a value
	// with mixed raw/percent-encoded colons becomes uniform. Falls back to the
	// original segment if it contains a malformed escape sequence.
	url.pathname = url.pathname
		.split('/')
		.map((segment) => {
			try {
				return encodeURIComponent(decodeURIComponent(segment));
			} catch {
				return segment;
			}
		})
		.join('/');

	return url.toString();
}
